// Libs
import clsx from "clsx";
import { pick } from "lodash";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

// Utils
import track from "src/utils/analytics";
import { usePageStyle } from "./Page.styles";

// Components
import { Container, type ContainerTypeMap } from "@mui/material";

const MAX_WIDTH_MAP: Record<PageProps["variant"], ContainerTypeMap["props"]["maxWidth"]> = {
    default: false,
    "full-width": false,
    "full-height": false,
    full: false,
    center: "lg",
    flex: false,
    ds: false,
    bare: false,
    "ds-without-padding": false,
};

const CONTAINER_MAP: Record<PageProps["variant"], React.ElementType | string> = {
    default: "div",
    "full-width": "div",
    "full-height": "div",
    full: "div",
    center: Container,
    flex: "div",
    ds: "div",
    bare: "div",
    "ds-without-padding": "div",
};

interface PageProps {
    className?: string;
    title?: string;
    variant?:
        | "default"
        | "full-width"
        | "full-height"
        | "full"
        | "center"
        | "flex"
        | "ds"
        | "bare"
        | "ds-without-padding";
    hideOverflow?: boolean;
}

export const Page = React.forwardRef<HTMLDivElement, React.PropsWithChildren<PageProps>>(
    function Page(
        { children, className, title = "", variant = "default", hideOverflow = false },
        ref
    ) {
        const location = useLocation();
        const classes = usePageStyle();

        useEffect(() => {
            track.pageview({
                page_path: location.pathname,
            });
        }, []);

        const ContainerComp = CONTAINER_MAP[variant];

        return (
            <>
                {title && (
                    <Helmet>
                        <title>{title}</title>
                    </Helmet>
                )}
                <ContainerComp
                    {...pick(
                        { maxWidth: MAX_WIDTH_MAP[variant] },
                        MAX_WIDTH_MAP[variant] && "maxWidth"
                    )}
                    className={clsx(
                        classes.root,
                        {
                            [classes.flex]: variant === "flex",
                            [classes.fullWidth]: ["full-width", "full"].includes(variant),
                            [classes.fullHeight]: ["full-height", "full"].includes(variant),
                            [classes.ds]: ["ds"].includes(variant),
                            [classes.bare]: ["bare"].includes(variant),
                            [classes.hideOverflow]: hideOverflow,
                            [classes.dsWithoutPadding]: ["ds-without-padding"].includes(variant),
                        },
                        className
                    )}
                    ref={ref}
                >
                    {children}
                </ContainerComp>
            </>
        );
    }
);

export default Page;
